/**
 * Shape morphing: outlines are sampled as N points around the perimeter
 * (t ∈ [0,1), starting at the top, clockwise) in a normalized 0..1 box, and
 * lerped point-by-point between source and target — a pure function of
 * `morphProgress`, so export seeking stays deterministic.
 */
import { getFlattenedPathPoints } from "./pen/flatten";

import type { PenPointHandles } from "./pen/types";

export const MORPH_SHAPES = {
  ellipse: 0,
  diamond: 1,
  rectangle: 2,
  triangle: 3,
  star: 4,
  heart: 5,
} as const;

export const MORPH_SHAPE_LABELS = [
  "Ellipse",
  "Diamond",
  "Rectangle",
  "Triangle",
  "Star",
  "Heart",
];

type Point = [number, number];

/** Cumulative-length tables per vertex array — polygonPoint runs 64× per
 * frame during a morph, and rebuilding the table per sample was the audit's
 * clearest per-frame recompute (O(n·samples)). Vertex arrays are immutable
 * (module constants + cached elementOutlinePolygon results), so identity
 * keying is safe. */
const lengthTableCache = new WeakMap<
  Point[],
  { lengths: number[]; total: number }
>();

const lengthTable = (vertices: Point[]) => {
  let entry = lengthTableCache.get(vertices);
  if (!entry) {
    const lengths: number[] = [];
    let total = 0;
    for (let i = 0; i < vertices.length; i++) {
      const [x1, y1] = vertices[i];
      const [x2, y2] = vertices[(i + 1) % vertices.length];
      const len = Math.hypot(x2 - x1, y2 - y1);
      lengths.push(len);
      total += len;
    }
    entry = { lengths, total };
    lengthTableCache.set(vertices, entry);
  }
  return entry;
};

/** Walks a closed polygon's perimeter: t ∈ [0,1) → point on the outline. */
const polygonPoint = (vertices: Point[], t: number): Point => {
  const { lengths, total } = lengthTable(vertices);
  let remaining = (((t % 1) + 1) % 1) * total;
  for (let i = 0; i < vertices.length; i++) {
    if (remaining <= lengths[i]) {
      const f = lengths[i] === 0 ? 0 : remaining / lengths[i];
      const [x1, y1] = vertices[i];
      const [x2, y2] = vertices[(i + 1) % vertices.length];
      return [x1 + (x2 - x1) * f, y1 + (y2 - y1) * f];
    }
    remaining -= lengths[i];
  }
  return vertices[0];
};

/** Public alias: walk a closed normalized polygon's outline at t ∈ [0,1). */
export const sampleOutlinePoints = polygonPoint;

const RECT_VERTICES: Point[] = [
  [0.5, 0],
  [1, 0],
  [1, 1],
  [0, 1],
  [0, 0],
];

const DIAMOND_VERTICES: Point[] = [
  [0.5, 0],
  [1, 0.5],
  [0.5, 1],
  [0, 0.5],
];

const TRIANGLE_VERTICES: Point[] = [
  [0.5, 0],
  [1, 1],
  [0, 1],
];

const STAR_VERTICES: Point[] = Array.from({ length: 10 }, (_, i) => {
  const angle = -Math.PI / 2 + (i * Math.PI) / 5;
  const r = i % 2 === 0 ? 0.5 : 0.21;
  return [0.5 + r * Math.cos(angle), 0.5 + r * Math.sin(angle)] as Point;
});

/** t ∈ [0,1) → outline point of `shape` in a normalized 0..1 box. */
export const sampleMorphShape = (shape: number, t: number): Point => {
  switch (shape) {
    case MORPH_SHAPES.ellipse: {
      const angle = -Math.PI / 2 + t * 2 * Math.PI;
      return [0.5 + 0.5 * Math.cos(angle), 0.5 + 0.5 * Math.sin(angle)];
    }
    case MORPH_SHAPES.diamond:
      return polygonPoint(DIAMOND_VERTICES, t);
    case MORPH_SHAPES.rectangle:
      return polygonPoint(RECT_VERTICES, t);
    case MORPH_SHAPES.triangle:
      return polygonPoint(TRIANGLE_VERTICES, t);
    case MORPH_SHAPES.star:
      return polygonPoint(STAR_VERTICES, t);
    case MORPH_SHAPES.heart: {
      // classic parametric heart, mapped into the unit box, starting at the
      // top notch and walking clockwise like the polygon shapes
      const a = Math.PI - t * 2 * Math.PI;
      const x = 16 * Math.sin(a) ** 3;
      const y =
        13 * Math.cos(a) -
        5 * Math.cos(2 * a) -
        2 * Math.cos(3 * a) -
        Math.cos(4 * a);
      return [0.5 + x / 34, 0.5 - y / 34];
    }
    default:
      return polygonPoint(RECT_VERTICES, t);
  }
};

/** Maps an excalidraw element type onto its morph-outline shape id. */
export const morphShapeForElementType = (type: string): number => {
  switch (type) {
    case "ellipse":
      return MORPH_SHAPES.ellipse;
    case "diamond":
      return MORPH_SHAPES.diamond;
    default:
      return MORPH_SHAPES.rectangle;
  }
};

/**
 * An element's outline as a closed polygon of normalized (0..1 bbox)
 * vertices — the sampling source for "morph to element" and the morph
 * source for path elements. Linear/freedraw elements use their own points
 * (normalized to the points' bbox, downsampled to ≤64 vertices); open paths
 * are walked out-and-back so the closed polygon degenerates to the drawn
 * line itself (no spurious closing chord at progress 0/1). Shapes use their
 * built-in morph outline; anything else falls back to the rectangle.
 */
export const elementOutlinePolygon = (element: {
  type: string;
  versionNonce?: number;
  points?: readonly (readonly number[])[];
  handles?: readonly (PenPointHandles | null)[] | null;
}): Point[] => {
  // mutateElement mutates elements IN PLACE and bumps versionNonce — cache
  // per object identity, invalidated by the nonce (audit: this re-walked and
  // re-normalized the whole path on every animated frame)
  const cached = outlineCache.get(element);
  if (cached && cached.versionNonce === (element.versionNonce ?? 0)) {
    return cached.polygon;
  }
  const polygon = computeOutlinePolygon(element);
  outlineCache.set(element, {
    versionNonce: element.versionNonce ?? 0,
    polygon,
  });
  return polygon;
};

const outlineCache = new WeakMap<
  object,
  { versionNonce: number; polygon: Point[] }
>();

const computeOutlinePolygon = (element: {
  type: string;
  points?: readonly (readonly number[])[];
  handles?: readonly (PenPointHandles | null)[] | null;
}): Point[] => {
  // pen paths (bezier handles) walk the actual curve, not the anchors
  const pts =
    element.points && element.points.length >= 2
      ? getFlattenedPathPoints(
          element as unknown as Parameters<typeof getFlattenedPathPoints>[0],
        )
      : element.points;
  if (
    (element.type === "line" ||
      element.type === "arrow" ||
      element.type === "freedraw") &&
    pts &&
    pts.length >= 2
  ) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y] of pts) {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
    const w = maxX - minX || 1;
    const h = maxY - minY || 1;
    const first = pts[0];
    const last = pts[pts.length - 1];
    // closed when endpoints (nearly) meet, relative to the path's own bbox
    const isClosed =
      Math.hypot((last[0] - first[0]) / w, (last[1] - first[1]) / h) < 0.05;
    // open paths double up (out-and-back), so halve the budget to keep ≤64
    const step = Math.max(1, Math.ceil(pts.length / (isClosed ? 64 : 32)));
    const out: Point[] = [];
    for (let i = 0; i < pts.length; i += step) {
      out.push([(pts[i][0] - minX) / w, (pts[i][1] - minY) / h]);
    }
    if (!isClosed) {
      for (let i = out.length - 2; i > 0; i--) {
        out.push(out[i]);
      }
    }
    return out;
  }
  const shape = morphShapeForElementType(element.type);
  const SAMPLES = 32;
  return Array.from({ length: SAMPLES }, (_, i) =>
    sampleMorphShape(shape, i / SAMPLES),
  );
};
